import { useEffect } from 'react';
import { getDocument } from '@atlaskit/browser-apis';

// Find editor node dom with localId - similar to confluence useScrollOnUrlChange.ts
var getLocalIdSelector = function getLocalIdSelector(localId, container) {
  // Check if the element with data-local-id exists
  var element = container.querySelector("[data-local-id=\"".concat(localId, "\"]"));
  if (element) {
    return element;
  }

  // Special case for decision lists and task lists which already have localId
  element = container.querySelector("[data-decision-list-local-id=\"".concat(localId, "\"]"));
  if (element) {
    return element;
  }
  element = container.querySelector("[data-task-list-local-id=\"".concat(localId, "\"]"));
  if (element) {
    return element;
  }

  // Special case for tables which use data-table-local-id
  element = container.querySelector("[data-table-local-id=\"".concat(localId, "\"]"));
  if (element) {
    return element;
  }

  // Special case for extension, smart cards and media which use lowercase localid
  element = container.querySelector("[localid=\"".concat(localId, "\"]"));
  if (element) {
    return element;
  }
  return null;
};
export var useScrollToLocalId = function useScrollToLocalId(containerRef, shouldScrollToLocalId) {
  useEffect(function () {
    var _getDocument;
    // Only run in browser environment
    if (typeof window === 'undefined' || !(containerRef !== null && containerRef !== void 0 && containerRef.current) || !shouldScrollToLocalId) {
      return;
    }

    // Parse URL parameters for block ID
    var urlParams = new URLSearchParams(window.location.search);
    var blockId = urlParams.get('block');
    if (!blockId) {
      return;
    }
    var retryCount = 0;
    // 5 second timeout based on dashboard showing ~4.96s TTI load time
    var maxRetries = 20; // Maximum 20 retries (5 seconds at 250ms intervals)
    var retryInterval = 250; // Check every 250ms
    var intervalId = null;
    var scrollToElement = function scrollToElement() {
      if (containerRef !== null && containerRef !== void 0 && containerRef.current) {
        var element = getLocalIdSelector(blockId, containerRef.current);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
          return true;
        }
      }
      return false;
    };
    var attemptScroll = function attemptScroll() {
      retryCount++;

      // Try to scroll to element
      if (scrollToElement()) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return true;
      }

      // Stop retrying if we've exceeded max retries
      if (retryCount >= maxRetries) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return false;
      }
      return false;
    };

    // Try to scroll immediately
    if (attemptScroll()) {
      return;
    }
    if (((_getDocument = getDocument()) === null || _getDocument === void 0 ? void 0 : _getDocument.readyState) === 'complete') {
      // Document is already ready, try a few more times with delays
      // This handles cases where elements are added after document ready
      intervalId = setInterval(function () {
        attemptScroll();
      }, retryInterval);
    } else {
      // Document not ready yet, wait for it and then retry
      intervalId = setInterval(function () {
        var _getDocument2;
        if (((_getDocument2 = getDocument()) === null || _getDocument2 === void 0 ? void 0 : _getDocument2.readyState) === 'complete') {
          attemptScroll();
        } else if (retryCount >= maxRetries) {
          // Stop retrying even if document isn't ready
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        } else {
          retryCount++;
        }
      }, retryInterval);
    }

    // Cleanup function
    return function () {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [containerRef, shouldScrollToLocalId]);
};